import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from '@narvar/moment-shim';
import cx from 'classnames';
import { a11ySelection } from '../../lib/a11yUtils';
import { currencyService } from '../../services/currency-service/currency-service';

import s from './returnMethod.module.css';
import step2Styles from '../../controllers/Step2Step3Ctrl/step2Step3Ctrl.module.css';
import { ShippingAddress } from '../ShippingAddress';
import { SelectCheckmark } from '../SelectCheckmark';
import generateMethodKey from '../../lib/generateMethodKey';
import { PICKUP } from '../../constants/returnMethods';
import {
  retailerSettingsService,
  returnFeeService,
  returnItemsService,
  returnMethodSidebar,
  scheduledPickup,
} from '../../services';
import { isAddressValid } from '../../lib/addresses';
import { store } from '../../store';
import { updateStep2Complete } from '../../actions/page';
import { setIsValidAddress } from '../../actions/addresses';

const mapStateToProps = ({
  language,
  returnMethods: { returnMethods, selectedMethodKey } = {},
  addresses: { isValidAddress } = {},
  carrierPickup: {
    date,
    address,
    address: {
      address_line1: addressLine1 = '',
      address_line2: addressLine2 = '',
      city = '',
      province = '',
      postal_code: postalCode = '',
    } = {},
  },
}) => ({
  isValidAddress,
  language,
  returnMethods,
  selectedMethodKey,
  pickupAddress: address,
  pickupDate: date && moment(date.toString()).format('dddd, LL'),
  pickupAddressLine1: addressLine1,
  pickupAddressLine2: addressLine2,
  pickupAddressLine3: province ? `${city}, ${province}, ${postalCode}` : `${city}, ${postalCode}`,
});

const Component = ({
  isValidAddress,
  returnMethods,
  selectedMethodKey,
  language,
  pickupDate,
  pickupAddressLine1,
  pickupAddressLine2,
  pickupAddressLine3,
  toStep3,
}) => {
  const isShowSideBar = (returnMethod) => {
    const {
      page: { locale, giftOrder = false } = {},
      returnOptions: { enabled: returnOptionsEnabled = false } = {},
      addresses: { shipTo } = {},
    } = store.getState();
    const exchangesEnabled = retailerSettingsService.getIsExchangeEnabled();
    const hasAnExchange = returnItemsService.hasAnExchange();
    const useGiftAddressSidebar = giftOrder && locale === 'en_US' && !returnOptionsEnabled;
    let showSidebar = false;

    if (returnMethod.get_address) {
      showSidebar = true;
      if (exchangesEnabled && !hasAnExchange) {
        showSidebar = false;
      }
    }

    if (returnMethod.checkShipToAddress) {
      if (isAddressValid(shipTo)) {
        showSidebar = false;
      }
    }

    if (useGiftAddressSidebar) {
      showSidebar = true;
    }

    return showSidebar;
  };

  const openAddressPopup = (method) => {
    const {
      addresses: { shippingAddress } = {},
      page: { locale, giftOrder = false } = {},
      returnOptions: { enabled: returnOptionsEnabled = false } = {},
    } = store.getState();
    const settings = retailerSettingsService.getSettings();
    const hasSidebarAddressConfig =
      settings.sidebarShippingMethodConfig && settings.sidebarShippingMethodConfig.length > 0;
    const useGiftAddressSidebar = giftOrder && locale === 'en_US' && !returnOptionsEnabled;
    const isShippingAddressEmpty = Object.keys(shippingAddress).length === 0;

    if ((hasSidebarAddressConfig && method.get_address) || useGiftAddressSidebar) {
      returnMethodSidebar
        .openSidebar()
        .then((response) => {
          if (method.id === PICKUP && !pickupDate) return;
          store.dispatch(setIsValidAddress(response));
          returnFeeService.getFee(method, toStep3);
          store.dispatch(updateStep2Complete(!isShippingAddressEmpty));
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  const openSidebar = (method) => {
    const {
      page: { locale, giftOrder = false } = {},
      returnOptions: {
        enabled: returnOptionsEnabled = false,
        methods: returnOptionsMethods = {},
        selectedOptionId,
      } = {},
    } = store.getState();
    const carrierPickupEnabled = selectedOptionId === PICKUP;
    const settings = retailerSettingsService.getSettings();
    const hasSidebarAddressConfig =
      settings.sidebarShippingMethodConfig && settings.sidebarShippingMethodConfig.length > 0;
    const hasFormAddressConfig =
      returnOptionsEnabled &&
      Object.values(returnOptionsMethods).some(
        ({ get_address: getAddress }) => getAddress === true && !carrierPickupEnabled,
      );
    const useGiftAddressSidebar = giftOrder && locale === 'en_US' && !returnOptionsEnabled;
    if (!hasSidebarAddressConfig && method.get_address) {
      console.error(
        'This method has been configured to ' +
          'require an address but no configured address ' +
          'form was found. Please configure an address ' +
          'form or remove the get address requirement.',
      );
      return;
    }
    if (
      (hasSidebarAddressConfig && method.get_address && !hasFormAddressConfig) ||
      useGiftAddressSidebar
    ) {
      openAddressPopup(method);
    } else {
      console.error('Error opening sidebar.');
    }
  };

  const clickAction = (method) => {
    if (method.id === PICKUP) {
      scheduledPickup.actions.initialize({
        return_carrier: method.carrier,
        pickupDateRequired: true,
      });
      scheduledPickup.actions.open();
      scheduledPickup.actions.fetchPickupDates();
    }

    return isShowSideBar(method) ? openSidebar(method) : returnFeeService.getFee(method, toStep3);
  };

  return (
    <ul className={step2Styles.return_method_list}>
      {returnMethods.map((method, methodIndex) => {
        const isSelected = generateMethodKey(method) === selectedMethodKey;
        const selectedClass = isSelected ? `${s.method_selected} selected-method` : '';
        const {
          id,
          carrier,
          method_price_label: priceLabel,
          return_cost: { return_shipping_fee: shippingFee },
        } = method;
        const pickupTitle =
          language[`returns.return_methods.${id}_${carrier}.pickup_details_title`];
        let returnMethodPrice = '';
        if (priceLabel && language[priceLabel]) {
          returnMethodPrice = language[priceLabel];
        } else if (shippingFee) {
          returnMethodPrice = currencyService.formatPrice(shippingFee);
        } else {
          returnMethodPrice = language['returns.general.free_label'];
        }

        return (
          <li>
            <return-method>
              <div
                className={cx(s.method_container, 'return-method-container', selectedClass)}
                onClick={a11ySelection(() => clickAction(method))}
                onKeyDown={a11ySelection(() => clickAction(method))}
                aria-label="Return Method"
                role="button"
                tabIndex="0"
              >
                <div className={s.method_info}>
                  <h4
                    className={`return-method-${methodIndex}-title ${s.method_title}`}
                    dangerouslySetInnerHTML={{ __html: language[method.title] }}
                  />
                  {language[method.description] && <h5>{language[method.description]}</h5>}
                  <ul className={s.feature_list}>
                    {method.features.map((feature, featureIndex) => (
                      <li
                        className={`return-method-${methodIndex}-feature-${featureIndex} ${s.feature_list_item}`}
                        dangerouslySetInnerHTML={{ __html: language[feature.feature] }}
                      />
                    ))}
                  </ul>
                  {method.store_link && language[method.store_link] && (
                    <div className={`${s.store_link_container} find_store`}>
                      <a
                        className={`store_link return-method-${methodIndex}-store-link`}
                        target="_blank"
                        rel="noopener noreferrer"
                        href={language[method.store_link]}
                      >
                        {language[method.store_link_text]}
                      </a>
                    </div>
                  )}
                </div>

                <div>
                  <SelectCheckmark selected={isSelected} />
                </div>
                <div className={`method-price-container ${s.method_price_container}`}>
                  <p className={`return-method-${methodIndex}-price ${s.method_price}`}>
                    {returnMethodPrice}
                  </p>
                </div>
                <div className="clearfix" />
                {method.get_address && isValidAddress && (
                  <shipping-address class={s.return_method_address_block}>
                    <ShippingAddress />
                  </shipping-address>
                )}
                {method.id === 'pickup' && pickupDate && (
                  <div className={s.pickup_container}>
                    {pickupTitle && <h5>{pickupTitle}</h5>}
                    <p>{pickupDate}</p>
                    <p>{pickupAddressLine1}</p>
                    {pickupAddressLine2 && <p>{pickupAddressLine2}</p>}
                    <p>{pickupAddressLine3}</p>
                  </div>
                )}
              </div>
            </return-method>
          </li>
        );
      })}
    </ul>
  );
};

Component.propTypes = {
  returnMethods: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedMethodKey: PropTypes.string.isRequired,
  language: PropTypes.object.isRequired,
  returnMethodPrice: PropTypes.string.isRequired,
  pickupTitle: PropTypes.string.isRequired,
  pickupDate: PropTypes.string.isRequired,
  pickupAddressLine1: PropTypes.string.isRequired,
  pickupAddressLine2: PropTypes.string.isRequired,
  pickupAddressLine3: PropTypes.string.isRequired,
  isValidAddress: PropTypes.bool.isRequired,
  toStep3: PropTypes.func.isRequired,
};

export const ReturnMethods = connect(mapStateToProps)(Component);
